import { Injectable } from '@angular/core';

import { ListResponse } from '../../models/list-response';
import { ApiService } from '../api/api.service';
import {
  Association, AssociationAlert,
  AssociationFilters,
  AssociationManager,
  AssociationManagerFilters, AssociationMember,
  AssociationMemberFilters
} from '../../models/association';
import { ActivityLogEntry } from '../../models/activity-log';

@Injectable({
  providedIn: 'root'
})
export class AssociationService {
  /**
   * A service providing access to associations via the API.
   */
  constructor(
    private apiService: ApiService
  ) {}

  getAssociations(pageNumber?: number,
                  pageSize?: number,
                  filters?: AssociationFilters): Promise<ListResponse<Association>> {
    return this.apiService.getCollection<Association, AssociationFilters>(
      'associations/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getAssociation(id: number): Promise<Association> {
    return this.apiService.getItem<Association>(
      'associations/',
      id
    );
  }

  addAssociation(association: Association): Promise<Association> {
    return this.apiService.postToAPI<Association>(
      'associations/',
      {
        name: association.name
      }
    );
  }

  updateAssociation(association: Association, profileImageFileData?: File): Promise<Association> {
    const patchPromise = this.apiService.patchToAPI<Association>(
      association.url,
      {
        name: association.name,
        verification_requirements: association.verification_requirements,
        automatic_removal_interval_months: association.automatic_removal_interval_months,
        automatic_removal_dates: association.automatic_removal_dates,
        reverification_period_days: association.reverification_period_days,
        show_reverification_alert: association.show_reverification_alert,
        allow_automatic_entry_pass_use: association.allow_automatic_entry_pass_use,
        membership_expiration_age: association.membership_expiration_age,
        allow_customer_applications: association.allow_customer_applications,
        application_document_field_config: association.application_document_field_config,
        application_text_field_config: association.application_text_field_config,
        application_document_field_name: association.application_document_field_name,
        application_text_field_name: association.application_text_field_name,
        application_customer_instructions: association.application_customer_instructions,
        auto_add_on_event_booking_types: association.auto_add_on_event_booking_types,
        auto_add_on_event_booking_categories: association.auto_add_on_event_booking_categories,
        auto_add_on_event_check_in_types: association.auto_add_on_event_check_in_types,
        auto_add_on_event_check_in_categories: association.auto_add_on_event_check_in_categories,
        auto_add_on_entry_pass_use_pass_types: association.auto_add_on_entry_pass_use_pass_types,
        auto_add_on_entry_pass_use_only_first: association.auto_add_on_entry_pass_use_only_first,
        auto_add_on_guest_pass_check_in: association.auto_add_on_guest_pass_check_in,
        auto_add_on_guest_pass_check_in_only_first: association.auto_add_on_guest_pass_check_in_only_first,
        member_square_customer_group_id: association.member_square_customer_group_id,
        member_lightspeed_customer_group_id: association.member_lightspeed_customer_group_id
      }
    );

    // If a profile image file was provided, upload that after the initial PATCH.
    if (profileImageFileData) {
      return patchPromise.then(updatedAssociation => {
        const profileImageFormData = new FormData();
        profileImageFormData.append(
          'profile_image',
          profileImageFileData,
          profileImageFileData.name
        );
        return this.apiService.patchToAPI<Association>(
          updatedAssociation.url,
          profileImageFormData,
          true
        );
      });
    } else {  // Otherwise, just return the initial PATCH's promise.
      return patchPromise;
    }
  }

  removeAssociation(associationUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      associationUrl
    ).then();
  }

  getAssociationManagers(pageNumber?: number,
                         pageSize?: number,
                         filters?: AssociationManagerFilters): Promise<ListResponse<AssociationManager>> {
    return this.apiService.getCollection<AssociationManager, AssociationManagerFilters>(
      'association-managers/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getAssociationManager(id: number): Promise<AssociationManager> {
    return this.apiService.getItem<AssociationManager>(
      'association-managers/',
      id
    );
  }

  addAssociationManager(associationManager: AssociationManager): Promise<AssociationManager> {
    return this.apiService.postToAPI<AssociationManager>(
      'association-managers/',
      associationManager
    );
  }

  removeAssociationManager(associationManagerUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      associationManagerUrl
    ).then();
  }

  getAssociationMembers(pageNumber?: number,
                        pageSize?: number,
                        filters?: AssociationMemberFilters): Promise<ListResponse<AssociationMember>> {
    return this.apiService.getCollection<AssociationMember, AssociationMemberFilters>(
      'association-members/',
      pageNumber,
      pageSize,
      filters
    );
  }

  getAssociationMember(id: number): Promise<AssociationMember> {
    return this.apiService.getItem<AssociationMember>(
      'association-members/',
      id
    );
  }

  addAssociationMember(associationMember: AssociationMember): Promise<AssociationMember> {
    return this.apiService.postToAPI<AssociationMember>(
      'association-members/',
      associationMember
    );
  }

  /**
   * Add a single customer to an association. Equivalent to
   * `addAssociationMember`, but takes only the two writable fields, so callers
   * adding many customers need not build an `AssociationMember` literal each time.
   */
  addCustomerToAssociation(associationId: number,
                           customerId: number): Promise<AssociationMember> {
    return this.apiService.postToAPI<AssociationMember>(
      'association-members/',
      {
        association_id: associationId,
        customer_id: customerId
      }
    );
  }

  addAssociationMemberByNameAndBirthday(associationId: number,
                                        firstName: string,
                                        lastName: string,
                                        birthday: string,
                                        email: string | null): Promise<void> {
    return this.apiService.postToAPI<any>(
      'associations/' + associationId.toString() + '/add-member-by-name-and-birthday/',
      {
        first_name: firstName,
        last_name: lastName,
        birthday,
        email
      }
    ).then();
  }

  removeAssociationMember(associationMemberUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      associationMemberUrl
    ).then();
  }

  reverifyAssociationMember(associationMemberId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'association-members/' + associationMemberId.toString() + '/reverify/',
      {}
    ).then();
  }

  approveAssociationJoinRequest(associationMemberId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'association-members/' + associationMemberId.toString() + '/join-request/approve/',
      {}
    ).then();
  }

  hideAssociationJoinRequest(associationMemberId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'association-members/' + associationMemberId.toString() + '/join-request/hide/',
      {}
    ).then();
  }

  getAssociationAlerts(associationId: number,
                       pageNumber?: number,
                       pageSize?: number): Promise<ListResponse<AssociationAlert>> {
    return this.apiService.getCollectionNoFilters<AssociationAlert>(
      'associations/' + associationId.toString() + '/alerts/',
      pageNumber,
      pageSize
    );
  }

  addAssociationAlert(associationId: number,
                      alert: AssociationAlert): Promise<AssociationAlert> {
    if (alert.expiry_date === '') {
      alert.expiry_date = null;
    }

    return this.apiService.postToAPI<AssociationAlert>(
      'associations/' + associationId.toString() + '/alerts/',
      alert
    );
  }

  removeAssociationAlert(alertUrl: string): Promise<void> {
    return this.apiService.deleteFromAPI<any>(
      alertUrl
    ).then();
  }

  getAssociationActivityLog(associationId: number,
                            pageNumber?: number,
                            pageSize?: number): Promise<ListResponse<ActivityLogEntry>> {
    return this.apiService.getCollectionNoFilters<ActivityLogEntry>(
      'associations/' + associationId.toString() + '/activity-log/',
      pageNumber,
      pageSize
    );
  }
}
